﻿import { useMutation, useQueryClient } from "@tanstack/react-query";
import projectService from "../../../api/projectService";
import { projectManagementQueryKeys } from "./useProjectQueries";
import type { Identifier, BaselineSaveMode } from "../types";

export function useProjectMutations({
  activeProjectId,
  categoryId,
}: {
  activeProjectId: Identifier | null;
  categoryId: string | null;
}) {
  const queryClient = useQueryClient();

  const invalidateProjectData = async (projectId = activeProjectId) => {
    await queryClient.invalidateQueries({
      queryKey: projectManagementQueryKeys.projects(categoryId),
    });
    await queryClient.invalidateQueries({
      queryKey: ["manpro", "provider", "projects"],
      exact: false,
    });
    if (projectId) {
      await queryClient.invalidateQueries({
        queryKey: projectManagementQueryKeys.projectTree(projectId),
      });
    }
  };

  const updateTaskProgressMutation = useMutation({
    mutationFn: async ({
      projectId,
      taskId,
      progress,
      notes,
    }: {
      projectId: Identifier;
      taskId: Identifier;
      progress: number;
      notes?: string;
    }) => projectService.updateTaskProgress(projectId, taskId, progress, notes || ""),
  });

  const toggleResourceMutation = useMutation({
    mutationFn: async ({
      projectId,
      resourceId,
    }: {
      projectId: Identifier;
      resourceId: Identifier;
    }) => projectService.toggleResourceUsed(projectId, resourceId),
  });

  const addExpenseMutation = useMutation({
    mutationFn: async ({
      projectId,
      data,
    }: {
      projectId: Identifier;
      data: any;
    }) => projectService.addExpense(projectId, data),
  });

  const assignTechniciansMutation = useMutation({
    mutationFn: async ({
      projectId,
      taskId,
      servicemanIds,
    }: {
      projectId: Identifier;
      taskId: Identifier;
      servicemanIds: Identifier[];
    }) => projectService.assignTechnicians(projectId, taskId, servicemanIds),
  });

  const validateTaskMutation = useMutation({
    mutationFn: async ({
      projectId,
      taskId,
      action,
      reason,
      validationResults,
    }: {
      projectId: Identifier;
      taskId: Identifier;
      action: "approve" | "reject";
      reason?: string;
      validationResults?: Record<string, boolean>;
    }) => projectService.validateTask(projectId, taskId, action, reason, validationResults),
  });

  const saveBaselineMutation = useMutation({
    mutationFn: async ({
      projectId,
      data,
      saveMode,
    }: {
      projectId: Identifier;
      data: Record<string, unknown>;
      saveMode: BaselineSaveMode;
    }) => projectService.saveBaseline(projectId, data, saveMode),
  });

  const createProjectMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) =>
      (await projectService.createProject(data)) as any,
  });

  const deleteProjectMutation = useMutation({
    mutationFn: async (projectId: Identifier) =>
      projectService.deleteProject(projectId),
  });

  const updateManagerNoteMutation = useMutation({
    mutationFn: async ({
      projectId,
      note,
    }: {
      projectId: Identifier;
      note: string;
    }) => projectService.updateManagerNote(projectId, note),
  });

  return {
    invalidateProjectData,
    updateTaskProgressMutation,
    toggleResourceMutation,
    addExpenseMutation,
    assignTechniciansMutation,
    validateTaskMutation,
    saveBaselineMutation,
    createProjectMutation,
    deleteProjectMutation,
    updateManagerNoteMutation,
  };
}
